import { mtFetch, type FetchResult } from "./mtFetch";

export type TerminologyItemType =
  | "glossary_term"
  | "abbreviation"
  | "term_alias"
  | "abbrev_candidate";

export interface ParentConceptSummary {
  concept_key?: string;
  source_text?: string;
  target_text?: string;
  source_lang?: string;
  target_lang?: string;
  domain?: string;
  source_key?: string;
  /** item_id родительского glossary_term — для перехода по клику. */
  item_id?: string;
}

export interface TerminologyItem {
  item_id: string;
  item_type: TerminologyItemType;
  domain?: string | null;
  source_lang?: string | null;
  target_lang?: string | null;
  source_text?: string | null;
  target_text?: string | null;
  canonical_text?: string | null;
  source_key?: string | null;
  source_ref?: string | null;
  source_expansion?: string | null;
  target_expansion?: string | null;
  /** Сокращение в target-языке (для item_type='abbreviation'). Например,
   *  для DOAC: target_abbreviation='ПОАК'. */
  target_abbreviation?: string | null;
  /** disambiguation-контекст смысла (для item_type='abbreviation'):
   *  предстеммленные слова + опц. веса. Правится в админке. */
  context_words_stems?: string[];
  context_weights?: Record<string, number>;
  status?: string | null;
  confidence?: number | null;
  external_ids?: Record<string, unknown>;
  notes?: string | null;
  payload?: Record<string, unknown>;
  created_at?: string | null;
  updated_at?: string | null;
  /** Для term_alias backend проставляет linkback на родительский glossary
   *  концепт (через concept_key), чтобы UI мог показать "alias для X→Y". */
  parent_concept?: ParentConceptSummary;
}

export interface TerminologyItemsList {
  schema_version: "medtranslate.admin.terminology_items.v1";
  total: number;
  count: number;
  limit: number;
  offset: number;
  filters: Record<string, string | number | undefined>;
  items: TerminologyItem[];
}

export interface TerminologyFilterOptions {
  schema_version: "medtranslate.admin.terminology_filter_options.v1";
  item_types: string[];
  domains: string[];
  statuses: string[];
  source_keys: string[];
}

export interface TerminologySibling {
  item_id: string;
  item_type: string;
  source_text?: string | null;
  source_expansion?: string | null;
  target_text?: string | null;
  target_expansion?: string | null;
  domain?: string | null;
  status?: string | null;
  external_ids?: Record<string, unknown> | null;
}

export interface ReplaceAliasSummary {
  item_id?: string;
  alias_text?: string | null;
  canonical_text?: string | null;
  alias_lang?: string | null;
  canonical_lang?: string | null;
  exact_replacement?: boolean;
  status?: string | null;
}

export interface TerminologyItemDetail {
  schema_version: "medtranslate.terminology.item.v1";
  item: TerminologyItem;
  siblings?: TerminologySibling[];
  replace_aliases?: ReplaceAliasSummary[];
}

export type ListTerminologyFilters = {
  q?: string;
  item_type?: string;
  domain?: string;
  status?: string;
  source_lang?: string;
  target_lang?: string;
  source_key?: string;
  /** Backend matches `external_ids.source_role`. Common values:
   *  `trusted_phrase_memory` (ex-enforcement candidates), etc. */
  source_role?: string;
  /** Только для glossary_term + status=approved. 'hard' — будет hard-replaced
   *  resolver'ом (constraint_eligible+morph_safe+trusted ru_term_source);
   *  'hint' — only LLM-prompt подсказка. */
  mode?: "hard" | "hint";
  limit?: number;
  offset?: number;
};

export function listTerminologyItems(
  filters: ListTerminologyFilters = {},
): Promise<FetchResult<TerminologyItemsList>> {
  return mtFetch<TerminologyItemsList>("/admin/terminology-items", {
    query: filters,
  });
}

export function listTerminologyFilterOptions(): Promise<
  FetchResult<TerminologyFilterOptions>
> {
  return mtFetch<TerminologyFilterOptions>("/admin/terminology-filter-options");
}

export function getTerminologyItem(
  itemId: string,
): Promise<FetchResult<TerminologyItemDetail>> {
  return mtFetch<TerminologyItemDetail>(
    `/admin/terminology-items/${encodeURIComponent(itemId)}`,
  );
}

export interface TerminologyUpdateResponse extends TerminologyItemDetail {
  /** Если на edit'е был установлен save_previous_as_alias=true и
   *  ru_term реально менялся — backend создаёт bridge-alias и возвращает
   *  его метаданные, чтобы UI мог показать toast/линк. */
  created_alias?: {
    alias_id: string;
    alias_text: string;
    canonical_text: string;
  };
  previous_item_id?: string;
}

export function updateTerminologyItem(
  itemId: string,
  body: Record<string, unknown>,
): Promise<FetchResult<TerminologyUpdateResponse>> {
  return mtFetch<TerminologyUpdateResponse>(
    `/admin/terminology-items/${encodeURIComponent(itemId)}`,
    { method: "POST", body },
  );
}

/** LLM-RAG подсказка смыслов аббревиатуры (POST /admin/abbrev-suggest). */
export interface AbbrevSenseSuggestion {
  source_expansion: string;
  target_expansion: string;
  target_abbreviation?: string;
  domain: string;
  context_words: string[];
  rationale?: string;
}

export interface AbbrevSuggestResponse {
  schema_version: string;
  surface: string;
  model_id: string;
  model?: string;
  senses: AbbrevSenseSuggestion[];
  rag: { known_sense_count: number | null; terminology_match_count: number };
}

export function suggestAbbrevSenses(
  surface: string,
  modelId?: string,
): Promise<FetchResult<AbbrevSuggestResponse>> {
  return mtFetch<AbbrevSuggestResponse>("/admin/abbrev-suggest", {
    method: "POST",
    body: { surface, ...(modelId ? { model_id: modelId } : {}) },
  });
}

/** «Проверка перевода на примере» (POST /admin/terminology-probe) —
 *  резолв против НЕсохранённых правок термина + перевод. Ничего не пишет в БД. */
export interface TerminologyProbeTerm {
  source_abbreviation?: string;
  source_lang?: string;
  source_expansion?: string;
  target_abbreviation?: string;
  target_lang?: string;
  target_expansion?: string;
  domain?: string;
  context_words?: string;
}

export interface TerminologyProbeMatch {
  surface?: string | null;
  kind?: string | null;
  output_abbreviation?: string | null;
  target_text?: string | null;
  domain?: string | null;
  context_score?: number | null;
}

export interface TerminologyProbeResponse {
  schema_version: string;
  expanded_text?: string | null;
  terms: TerminologyProbeMatch[];
  translation?: string | null;
}

export function probeTerminology(body: {
  text: string;
  source_lang?: string;
  target_lang?: string;
  term?: TerminologyProbeTerm;
  include_translation?: boolean;
}): Promise<FetchResult<TerminologyProbeResponse>> {
  return mtFetch<TerminologyProbeResponse>("/admin/terminology-probe", {
    method: "POST",
    body,
  });
}

/** Поиск смыслов аббревиатуры по короткой форме (GET /admin/abbreviations-by-short/{short}).
 *  Backbone для intent-launcher: ловит и EN, и RU аббревиатуру, отдаёт все смыслы. */
export interface AbbrevSenseRow {
  abbreviation_id: string;
  /** content-hash id для /admin/terminology-items/{id} (обогащается бэкендом). */
  item_id?: string;
  source_abbreviation?: string | null;
  source_lang?: string | null;
  source_expansion?: string | null;
  target_abbreviation?: string | null;
  target_lang?: string | null;
  target_expansion?: string | null;
  domain?: string | null;
  status?: string | null;
  confidence?: number | null;
  source_key?: string | null;
  context_words_stems?: string[];
}

export interface AbbrevCandidateRow {
  candidate_id: string;
  abbreviation?: string | null;
  definition?: string | null;
  is_ambiguous?: boolean;
  approved?: boolean;
  /** Клинреки/документы-источники — по их числу судим о частотности смысла. */
  source_refs?: string[];
  source_count?: number;
}

export interface AbbreviationsByShortResponse {
  schema_version: string;
  short: string;
  is_polysemic: boolean;
  summary: {
    approved_count: number;
    abbreviation_count: number;
    candidate_count: number;
  };
  abbreviations: AbbrevSenseRow[];
  candidates: AbbrevCandidateRow[];
}

export function getAbbreviationsByShort(
  short: string,
): Promise<FetchResult<AbbreviationsByShortResponse>> {
  return mtFetch<AbbreviationsByShortResponse>(
    `/admin/abbreviations-by-short/${encodeURIComponent(short)}`,
  );
}

/** Создать допустимый синоним (POST /admin/term-aliases — эндпоинт уже есть). */
export interface CreateSynonymAliasBody {
  canonical_text: string;
  alias_text: string;
  target_lang: "en" | "ru";
  domain: string;
  source_key: string;
  confidence?: number;
  concept_key?: string;
  notes?: string;
  reviewer?: string;
}

export function createSynonymAlias(
  body: CreateSynonymAliasBody,
): Promise<FetchResult<{ schema_version: string; alias_id: string }>> {
  return mtFetch("/admin/term-aliases", { method: "POST", body });
}

/** Создать новый термин/аббревиатуру с нуля (intent «добавить»). */
export interface TerminologyCreateResponse {
  schema_version: string;
  item: TerminologyItem;
}

export interface CreateGlossaryTermBody {
  source_text?: string; // en
  target_text?: string; // ru
  domain?: string;
  source_lang?: string;
  target_lang?: string;
  hard?: boolean; // жёсткая фиксация (external_ids enforcement)
  notes?: string;
  reviewer?: string;
}

export function createGlossaryTerm(
  body: CreateGlossaryTermBody,
): Promise<FetchResult<TerminologyCreateResponse>> {
  return mtFetch<TerminologyCreateResponse>("/admin/glossary-terms", {
    method: "POST",
    body,
  });
}

export interface CreateAbbreviationBody {
  source_abbreviation: string; // en
  source_expansion: string; // en
  target_expansion: string; // ru
  target_abbreviation?: string; // ru
  domain?: string;
  context_words?: string; // через запятую; бэкенд стеммит
  source_lang?: string;
  target_lang?: string;
  notes?: string;
  reviewer?: string;
}

export function createAbbreviation(
  body: CreateAbbreviationBody,
): Promise<FetchResult<TerminologyCreateResponse>> {
  return mtFetch<TerminologyCreateResponse>("/admin/abbreviations", {
    method: "POST",
    body,
  });
}

export interface DeleteTerminologyResponse {
  schema_version: string;
  deleted_item_id: string;
  deleted_alias_id: string;
  deleted_by: string;
}

export function deleteTerminologyItem(
  itemId: string,
): Promise<FetchResult<DeleteTerminologyResponse>> {
  return mtFetch<DeleteTerminologyResponse>(
    `/admin/terminology-items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
  );
}

export interface CreateReplaceAliasResponse {
  schema_version: string;
  alias_id: string;
  alias_text: string;
  canonical_text: string;
  external_ids: Record<string, unknown>;
  parent_item_id: string;
}

export interface CreateReplaceAliasBody {
  alias_text: string;
  canonical_text?: string;
  exact?: boolean;
}

export interface PromoteAbbrevCandidateResponse {
  schema_version: string;
  review_item_id: string;
  stable_key: string;
  status: string;
  candidate_id: string;
}

export function promoteAbbrevCandidateToReview(
  itemId: string,
  body: { note?: string } = {},
): Promise<FetchResult<PromoteAbbrevCandidateResponse>> {
  return mtFetch<PromoteAbbrevCandidateResponse>(
    `/admin/terminology-items/${encodeURIComponent(itemId)}/promote-to-review`,
    { method: "POST", body },
  );
}

export function addReplaceAliasForTerminologyItem(
  itemId: string,
  body: CreateReplaceAliasBody,
): Promise<FetchResult<CreateReplaceAliasResponse>> {
  return mtFetch<CreateReplaceAliasResponse>(
    `/admin/terminology-items/${encodeURIComponent(itemId)}/replace-aliases`,
    { method: "POST", body },
  );
}

export interface ReopenReviewResponse {
  schema_version: string;
  review_item_id: string;
  stable_key: string;
  item_type: string;
  status: string;
}

export function reopenReviewForTerminologyItem(
  itemId: string,
  body: { note?: string } = {},
): Promise<FetchResult<ReopenReviewResponse>> {
  return mtFetch<ReopenReviewResponse>(
    `/admin/terminology-items/${encodeURIComponent(itemId)}/reopen-review`,
    { method: "POST", body },
  );
}

export interface BulkTerminologyResult {
  schema_version: "medtranslate.admin.terminology_items_bulk.v1";
  reviewer: string;
  requested_count: number;
  success_count: number;
  error_count: number;
  patch: Record<string, unknown>;
  results: Array<{
    item_id: string;
    ok: boolean;
    error?: string;
    item?: TerminologyItem;
  }>;
}

export function bulkUpdateTerminologyItems(body: {
  reviewer: string;
  item_ids: string[];
  patch: Record<string, unknown>;
}): Promise<FetchResult<BulkTerminologyResult>> {
  return mtFetch<BulkTerminologyResult>("/admin/terminology-items-batch", {
    method: "POST",
    body,
  });
}
